/**
 * ADM-07a — canonical module/tab/sub-tab registry.
 *
 * Single source of truth for "every tab and sub-tab in the system" (HR's ADM-07
 * ask). This is the tree ADM-07b's per-node access-right storage, ADM-07c's
 * Access Rights tree UI, and ADM-07d's route-guard/sidebar enforcement will all
 * key off of. Extracted from `Sidebar.tsx`'s `NAV_GROUPS` (module -> tab) plus
 * an audit of every page with in-page tabs (tab -> sub-tab).
 *
 * This file is output only for ADM-07a: it is not imported or enforced
 * anywhere yet. Wiring it into Sidebar.tsx, AuthContext, or route guards is
 * ADM-07b/07c/07d's job, not this one's.
 *
 * Node key scheme (stable, collision-free, no separate id-mapping table needed):
 *   - module node key  = the module's `moduleName` (matches `MODULE_NAMES` in
 *     `services/adminApi.ts` and the `module_name` column backing today's
 *     module-level access rights).
 *   - tab node key     = the tab's route `path` (e.g. '/admin/companies').
 *   - sub-tab node key = `${parent tab path}#${slug of the sub-tab label}`
 *     (e.g. '/admin/users/new#app-access'). Sub-tabs are in-page tabs with no
 *     route of their own, so they borrow their parent's path plus a slug.
 */

export interface ModuleTreeNode {
  key: string;
  label: string;
  /** Route path, only set on nodes that are directly navigable (tabs, not sub-tabs). */
  path?: string;
  children?: ModuleTreeNode[];
}

export interface ModuleTreeModule extends ModuleTreeNode {
  /** Matches `MODULE_NAMES` / the `module_name` column on today's module-level access rights. */
  moduleName: string;
}

const slug = (label: string) =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const subTabs = (parentPath: string, labels: string[]): ModuleTreeNode[] =>
  labels.map((label) => ({ key: `${parentPath}#${slug(label)}`, label }));

export const MODULE_TREE: ModuleTreeModule[] = [
  {
    key: 'Administration',
    moduleName: 'Administration',
    label: 'Administration',
    children: [
      {
        key: '/admin/master',
        label: 'Master',
        path: '/admin/master',
        // ADM-27: Company + Branch shipped first (Branch reuses TMS's existing
        // master, see '/tms/branches'); Department/Designation/Employee Group/
        // Subjects/Country are follow-up sub-tasks per the ticket's own plan.
        // ADM-29: Race/Religion/Nationality/Education/Fund/Bank/Component
        // ("Additional Master Setup") transferred here from Payroll — FWL and
        // Daily stay under Payroll; "Career" deferred (no existing concept).
        children: subTabs('/admin/master', [
          'Company',
          'Branch',
          'Race',
          'Religion',
          'Nationality',
          'Education',
          'Fund',
          'Bank',
          'Component',
        ]),
      },
      { key: '/admin/access-rights', label: 'Access Rights', path: '/admin/access-rights' },
      {
        key: '/admin/users/new',
        label: 'Create User',
        path: '/admin/users/new',
        children: subTabs('/admin/users/new', ['Create User', 'App Access', 'Company Access', 'Department Access']),
      },
      {
        key: '/admin/users',
        label: 'User List',
        path: '/admin/users',
        children: subTabs('/admin/users', ['Active Users', 'Inactive Users']),
      },
      { key: '/admin/employee-access', label: 'Employee Access', path: '/admin/employee-access' },
      { key: '/admin/email-settings', label: 'Email & 2FA Settings', path: '/admin/email-settings' },
      { key: '/admin/approver-settings', label: 'Approver Settings', path: '/admin/approver-settings' },
    ],
  },
  {
    key: 'MAVIS-HR',
    moduleName: 'MAVIS-HR',
    label: 'Human Resources',
    children: [
      { key: '/hr/dashboard', label: 'Dashboard', path: '/hr/dashboard' },
      {
        key: '/hr/employees',
        label: 'Employees',
        path: '/hr/employees',
        children: subTabs('/hr/employees', [
          'General',
          'E-PAYROLL Basic 1',
          'E-PAYROLL Basic 2',
          'E-PAYROLL Additional 1',
          'E-PAYROLL Additional 2',
          'E-TMS',
          'E-LEAVE',
          'E-CLAIM',
          'E-APPRAISAL',
        ]),
      },
      { key: '/hr/personal-details', label: 'Employee Personal Details', path: '/hr/personal-details' },
      { key: '/hr/training', label: 'Training', path: '/hr/training' },
      { key: '/hr/feedback', label: 'Feedback', path: '/hr/feedback' },
      { key: '/hr/meeting-rooms', label: 'Meeting Rooms', path: '/hr/meeting-rooms' },
      { key: '/hr/org-chart', label: 'Organization Chart', path: '/hr/org-chart' },
      { key: '/hr/staff-personal-changes', label: 'Staff Personal Approve/Reject', path: '/hr/staff-personal-changes' },
      { key: '/hr/letters', label: 'Letter Editor', path: '/hr/letters' },
      { key: '/hr/policies', label: 'Policies', path: '/hr/policies' },
      { key: '/hr/memos', label: 'Memos', path: '/hr/memos' },
      { key: '/hr/candidates', label: 'Candidates', path: '/hr/candidates' },
      { key: '/hr/onboarding-setup', label: 'Onboarding / Offboarding Setup', path: '/hr/onboarding-setup' },
    ],
  },
  {
    key: 'MAVIS-TMS',
    moduleName: 'MAVIS-TMS',
    label: 'Time Management',
    children: [
      { key: '/tms/dashboard', label: 'Dashboard', path: '/tms/dashboard' },
      { key: '/tms/shifts', label: 'Shifts', path: '/tms/shifts' },
      { key: '/tms/auto-shifts', label: 'Auto Shift', path: '/tms/auto-shifts' },
      { key: '/tms/duty-rosters', label: 'Duty Roster', path: '/tms/duty-rosters' },
      { key: '/tms/attendance', label: 'Attendance', path: '/tms/attendance' },
      {
        key: '/tms/branches',
        label: 'Branches',
        path: '/tms/branches',
        // Sub-tabs live on the per-branch detail page (/tms/branches/:id), not
        // the list page itself — same node key scheme applies (borrows the
        // list path since the detail route is parameterized).
        children: subTabs('/tms/branches', ['Shifts', 'Employees']),
      },
      {
        key: '/tms/system-parameters',
        label: 'System Parameters',
        path: '/tms/system-parameters',
        children: subTabs('/tms/system-parameters', ['Settings', 'PH/Rest', 'Weekly OT']),
      },
      { key: '/tms/readers', label: 'Reader Status', path: '/tms/readers' },
      {
        key: '/tms/employee-schedule-import',
        label: 'Employee Schedule Import/Export',
        path: '/tms/employee-schedule-import',
      },
      { key: '/tms/branch-schedule', label: 'Branch Schedule', path: '/tms/branch-schedule' },
      {
        key: '/tms/allowances',
        label: 'Allowances',
        path: '/tms/allowances',
        children: subTabs('/tms/allowances', ['Daily', 'Staff']),
      },
      { key: '/tms/update-by-individual', label: 'Update by Individual', path: '/tms/update-by-individual' },
      { key: '/tms/update-by-department', label: 'Update by Department', path: '/tms/update-by-department' },
      { key: '/tms/reprocess-recalculate', label: 'Reprocess / Recalculate', path: '/tms/reprocess-recalculate' },
      {
        key: '/tms/reports',
        label: 'TMS Reports',
        path: '/tms/reports',
        children: subTabs('/tms/reports', ['Daily Attendance', 'Individual Attendance', 'Exception Report']),
      },
    ],
  },
  {
    key: 'MAVIS-Leave',
    moduleName: 'MAVIS-Leave',
    label: 'Leave Management',
    children: [
      { key: '/lms/dashboard', label: 'Dashboard', path: '/lms/dashboard' },
      {
        key: '/lms/system-settings',
        label: 'System Settings',
        path: '/lms/system-settings',
        children: subTabs('/lms/system-settings', ['General', 'E-Leave-I', 'E-Leave-II']),
      },
      { key: '/lms/masters/medical-claim-settings', label: 'Medical Claim Settings', path: '/lms/masters/medical-claim-settings' },
      { key: '/lms/masters/exclude-days', label: 'Exclude Days', path: '/lms/masters/exclude-days' },
      { key: '/lms/oil-credits', label: 'Off-in-Lieu', path: '/lms/oil-credits' },
      { key: '/lms/leave-adjustments', label: 'Credit Annual Leave', path: '/lms/leave-adjustments' },
      { key: '/lms/masters/block-al', label: 'Block Annual Leave', path: '/lms/masters/block-al' },
      {
        key: '/lms/maternity-paternity',
        label: 'Maternity / Paternity',
        path: '/lms/maternity-paternity',
        children: subTabs('/lms/maternity-paternity', ['Maternity', 'Paternity']),
      },
      { key: '/lms/leave-eligibility', label: 'Leave Eligibility', path: '/lms/leave-eligibility' },
      { key: '/lms/leave-types', label: 'Leave Types', path: '/lms/leave-types' },
      { key: '/lms/leave-groups', label: 'Leave Groups', path: '/lms/leave-groups' },
      { key: '/lms/leave-categories', label: 'Leave Categories', path: '/lms/leave-categories' },
      {
        key: '/lms/leave-applications',
        label: 'Leave Applications',
        path: '/lms/leave-applications',
        children: subTabs('/lms/leave-applications', [
          'Pending for Me',
          'My Approved/Rejected',
          'Pending Previous Level',
        ]),
      },
      { key: '/lms/apply-on-behalf', label: 'Apply on Behalf', path: '/lms/apply-on-behalf' },
      { key: '/lms/leave-recalculate', label: 'Leave Recalculate', path: '/lms/leave-recalculate' },
      { key: '/lms/year-end', label: 'Year-End Process', path: '/lms/year-end' },
      { key: '/lms/holidays', label: 'Holidays', path: '/lms/holidays' },
      {
        key: '/lms/reports',
        label: 'Leave Reports',
        path: '/lms/reports',
        children: subTabs('/lms/reports', [
          'Leave Status Report',
          'Leave History Report',
          'Leave Consolidate Report (Current Year)',
        ]),
      },
      { key: '/lms/fwa/settings', label: 'FWA Settings', path: '/lms/fwa/settings' },
      { key: '/lms/fwa/applications', label: 'FWA Applications', path: '/lms/fwa/applications' },
    ],
  },
  {
    key: 'MAVIS-Claim',
    moduleName: 'MAVIS-Claim',
    label: 'Claims',
    children: [
      { key: '/ecs/settings', label: 'Utility Settings', path: '/ecs/settings' },
      { key: '/ecs/masters/claim-date-ranges', label: 'Claim Date Ranges', path: '/ecs/masters/claim-date-ranges' },
      { key: '/ecs/masters/claim-groups', label: 'Claim Group/Claim/Sub-Claim', path: '/ecs/masters/claim-groups' },
      { key: '/ecs/masters/claim-limits', label: 'Claim Limits', path: '/ecs/masters/claim-limits' },
      {
        key: '/ecs/claims',
        label: 'Claim Applications',
        path: '/ecs/claims',
        children: subTabs('/ecs/claims', [
          'Pending for Me',
          'My Approved/Rejected',
          'Pending Previous Level',
        ]),
      },
      {
        key: '/ecs/reports',
        label: 'Claim Reports',
        path: '/ecs/reports',
        children: subTabs('/ecs/reports', ['Claim Status Report', 'Claim Report (by Group/Claim/Sub-Claim)']),
      },
    ],
  },
  {
    key: 'MAVIS-Appraisal',
    moduleName: 'MAVIS-Appraisal',
    label: 'Performance Appraisal',
    children: [
      { key: '/pas/settings', label: 'Settings', path: '/pas/settings' },
      { key: '/pas/masters/rating-groups', label: 'Rating Groups', path: '/pas/masters/rating-groups' },
      { key: '/pas/masters/grades', label: 'Grades', path: '/pas/masters/grades' },
      { key: '/pas/masters/templates', label: 'Appraisal Templates', path: '/pas/masters/templates' },
      {
        key: '/pas/assign',
        label: 'Assign Appraisal',
        path: '/pas/assign',
        children: subTabs('/pas/assign', ['Unassigned', 'Assigned', 'Reassign']),
      },
      { key: '/pas/approve', label: 'Approve Appraisal', path: '/pas/approve' },
      { key: '/pas/publish', label: 'Publish Appraisal', path: '/pas/publish' },
      {
        key: '/pas/reports',
        label: 'Appraisal Reports',
        path: '/pas/reports',
        children: subTabs('/pas/reports', [
          'Appraisal Summary',
          'Recommendation Report',
          'Bell Curve Report',
          'Appraisal Detail',
        ]),
      },
    ],
  },
  {
    key: 'MAVIS-Pay',
    moduleName: 'MAVIS-Pay',
    label: 'Payroll',
    children: [
      {
        key: '/payroll/dashboard',
        label: 'Dashboard',
        path: '/payroll/dashboard',
        children: subTabs('/payroll/dashboard', ['Analytics', 'Reminders']),
      },
      {
        key: '/payroll/system-settings',
        label: 'System Settings',
        path: '/payroll/system-settings',
        children: subTabs('/payroll/system-settings', [
          'Company Information',
          'Salary Calculation',
          'General Settings',
          'Reminder Settings',
        ]),
      },
      {
        key: '/payroll/pay-day-groups',
        label: 'Pay Day Group',
        path: '/payroll/pay-day-groups',
        // The create/edit wizard at /payroll/pay-day-groups/:id ('Set Week Day'
        // -> ... -> 'Unpaid Leave Pay Day') is a linear creation flow, not
        // independently-navigable tabs, so it isn't modeled as sub-tabs here.
      },
      { key: '/masters/sections', label: 'Sections', path: '/masters/sections' },
      { key: '/masters/designation-groups', label: 'Designation Groups', path: '/masters/designation-groups' },
      { key: '/payroll/masters/fwl', label: 'FWL', path: '/payroll/masters/fwl' },
      { key: '/payroll/masters/daily-rates', label: 'Daily Rate', path: '/payroll/masters/daily-rates' },
      { key: '/payroll/masters/holiday-groups', label: 'Holiday Group', path: '/payroll/masters/holiday-groups' },
      {
        key: '/payroll/masters/allowances-deductions',
        label: 'Allowances / Deductions',
        path: '/payroll/masters/allowances-deductions',
      },
      { key: '/payroll/structures', label: 'Salary Structures', path: '/payroll/structures' },
      {
        key: '/payroll/payslips',
        label: 'Payroll Runs & Payslips',
        path: '/payroll/payslips',
        // The run-cycle flow at /payroll/runs/:runId/cycle ('Initialize' ->
        // 'Link Attendance Data' -> 'Salary Adjustment') is a linear process
        // wizard, not independently-navigable tabs — same rationale as the
        // Pay Day Group wizard above.
      },
      { key: '/payroll/bank-submission', label: 'Bank Submission', path: '/payroll/bank-submission' },
      { key: '/payroll/cpf-submission', label: 'CPF Submission', path: '/payroll/cpf-submission' },
      {
        key: '/payroll/reports',
        label: 'Reports',
        path: '/payroll/reports',
        children: subTabs('/payroll/reports', [
          'Monthly Payroll Summary',
          'CPF Return',
          'Yearly Summary Report',
          'Allowance Report',
          'Employee List',
        ]),
      },
    ],
  },
];

/**
 * Nodes outside the module/access-rights system entirely — every authenticated
 * user can reach these regardless of module permissions (see
 * `AuthContext.hasModuleAccess`'s "legacy/bootstrap" fallback and
 * `Sidebar.tsx`'s unconditional Dashboard/My Self-Service links). Listed here
 * for "every tab in the system" completeness, but ADM-07b/07d should NOT gate
 * these behind the per-node access-right model without a separate decision —
 * they're intentionally global today.
 */
export const GLOBAL_NODES: ModuleTreeNode[] = [
  { key: '/dashboard', label: 'Dashboard', path: '/dashboard' },
  {
    key: '/ess',
    label: 'My Self-Service',
    path: '/ess',
    children: subTabs('/ess', ['Home', 'Payslips', 'Request Profile Change', 'Policies', 'Memos', 'Change Password']),
  },
];
